import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { CustomTableSortLabel } from 'src/components/CustomTableSortLabel';
import { CHANNEL } from 'src/data/channel';
import { SORT_DIRECTION } from 'src/data/common';
import { ACCOUNT_FIELD } from 'src/data/performance';

import { AccountRow } from './components/AccountRow';
import { EmptyStateRow } from './components/EmptyStateRow';
import { Loader } from './components/Loader';
import { useStyles } from './useStyles';

export const ACCOUNT_TABLE_COLUMNS = [
  { field: ACCOUNT_FIELD.name, title: 'Name', style: { width: 'auto' } },
  { field: ACCOUNT_FIELD.id, title: 'Account ID', style: { width: 151 } },
  { field: ACCOUNT_FIELD.connectedState, title: '', style: { width: 160 } },
];

const prepareAccountsTableColumns = (hideIdField) => {
  if (hideIdField) {
    return ACCOUNT_TABLE_COLUMNS.filter((column) => column.field !== ACCOUNT_FIELD.id);
  }
  return ACCOUNT_TABLE_COLUMNS;
};
const getColumnTitle = (column) => {
  return column.title;
};

const alignAccounts = ({ accounts, connectedAccounts, isTrainingModel }) => {
  if (isTrainingModel) {
    return [...connectedAccounts, ...accounts];
  } else {
    return accounts;
  }
};

export const AccountsTable = ({
  accounts,
  connectedAccounts,
  onAccountClick,
  onSortChange,
  sortField,
  sortDirection,
  limitedAccountData,
  channel,
  loading,
  anyAccountConnected,
  isTrainingModel,
  isChannelConnected,
}) => {
  const styles = useStyles();
  const isEmptyState = accounts.length === 0 && !loading;
  const createSortHandler = (field) => () => {
    onSortChange(field);
  };

  const alignedAccounts = alignAccounts({
    accounts,
    connectedAccounts,
    isTrainingModel,
  });

  const isBrazeChannel = channel === CHANNEL.braze;

  const columns = prepareAccountsTableColumns(isBrazeChannel);

  return (
    <TableContainer className={styles.root}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.field}
                className={cx(styles.title)}
                style={column.style}
              >
                <CustomTableSortLabel
                  active={sortField === column.field}
                  direction={sortField === column.field ? sortDirection : SORT_DIRECTION.asc}
                  onClick={createSortHandler(column.field)}
                >
                  {getColumnTitle(column)}
                </CustomTableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <Loader />
          ) : (
            alignedAccounts?.map((account) => (
              <AccountRow
                key={account.id}
                account={account}
                onAccountClick={onAccountClick}
                featureLimitedLabel={
                  limitedAccountData?.accountId === account.id
                    ? `Limit reached, only ${limitedAccountData?.rowsCount}  rows imported`
                    : null
                }
                isConnected={connectedAccounts?.includes(account)}
                hideIdField={isBrazeChannel}
              />
            ))
          )}
          {isEmptyState && (
            <EmptyStateRow
              channel={channel}
              isChannelConnected={isChannelConnected}
              anyAccountConnected={anyAccountConnected}
              allAccountsConnected={!isTrainingModel && anyAccountConnected}
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

AccountsTable.propTypes = {
  accounts: PropTypes.arrayOf(PropTypes.shape({})),
  connectedAccounts: PropTypes.arrayOf(PropTypes.shape({})),
  onAccountClick: PropTypes.func,
  onSortChange: PropTypes.func,
  sortField: PropTypes.string,
  sortDirection: PropTypes.string,
  channel: PropTypes.string,
  loading: PropTypes.bool,
  limitedAccountData: PropTypes.shape(),
  anyAccountConnected: PropTypes.bool,
  isTrainingModel: PropTypes.bool,
  isChannelConnected: PropTypes.bool,
};

AccountsTable.defaultProps = {
  accounts: [],
  connectedAccounts: [],
  onAccountClick: null,
  onSortChange: null,
  sortField: null,
  sortDirection: null,
  channel: null,
  loading: false,
  limitedAccountData: null,
  anyAccountConnected: false,
  isTrainingModel: false,
  isChannelConnected: false,
};
